import { type PrimitiveType } from 'intl-messageformat';
import { type ReactNode } from 'react';
import type React from 'react';
import { useSnapshot } from 'valtio';

import { i18n } from './i18n-state';
import type { MessageDescriptor } from './intl-types';

type ComponentReplacement = (str: ReactNode) => React.JSX.Element;

type RequiredDescriptorFields = Pick<MessageDescriptor, 'id' | 'defaultMessage'>;
interface GetMessageParamsPrimitive {
  message?: RequiredDescriptorFields;
  values?: Record<string, PrimitiveType>;
}

interface GetMessageParams {
  message?: RequiredDescriptorFields;
  values?: Record<string, PrimitiveType | ComponentReplacement>;
}

/**
 * A hook that that returns the translated message string of a message descriptor if it is ready,
 * or the fallback message (the default one, defined in the message descriptor) if it is not.
 *
 * If the message is undefined, it returns the empty string ('').
 */
export function useMessageString({ message, values }: GetMessageParamsPrimitive): string {
  const { intl } = useSnapshot(i18n);

  return message !== undefined ? intl.formatMessage(message, values) : '';
}

/**
 * A hook that that returns a formatted message as a ReactNode, useful for when you need to pass a
 * component as a <wrapper> to a message, eg for bolding text etc. If the message is undefined,
 * it returns the empty string ('').
 */
export function useMessage({ message, values }: GetMessageParams): ReactNode {
  const { intl } = useSnapshot(i18n);

  return message !== undefined ? intl.formatMessage(message, values) : '';
}
